/**
 * Formats a `Date` as a human-readable signed-at label for the Sign Queue card.
 *
 * @param date - The moment the affidavit was signed (or queued offline).
 * @returns `"Today at 2:47 PM"` when the date is today in local time,
 *   otherwise `"Jun 9 at 2:47 PM"`.
 */
export function formatSignedAt(date: Date): string {
  const timeStr = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(date);
  const today = new Date();
  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth()    === today.getMonth()    &&
    date.getDate()     === today.getDate();
  if (isToday) return `Today at ${timeStr}`;
  const dateStr = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric',
  }).format(date);
  return `${dateStr} at ${timeStr}`;
}

/**
 * Returns the English ordinal suffix (`st`/`nd`/`rd`/`th`) for a day-of-month number.
 *
 * @param day - Day of month, `1`-`31`.
 * @returns The two-letter ordinal suffix, e.g. `'st'` for `1`/`21`/`31`, `'th'` for `11`-`13`.
 */
function ordinalSuffix(day: number): string {
  const rem100 = day % 100;
  if (rem100 >= 11 && rem100 <= 13) return 'th';
  switch (day % 10) {
    case 1:  return 'st';
    case 2:  return 'nd';
    case 3:  return 'rd';
    default: return 'th';
  }
}

/**
 * Formats a `Date` as the ordinal-day affidavit signing date — the
 * `#DATE#` / `id="date"` substitution value on the affidavit signing screen.
 *
 * @param date - The moment the affidavit is being signed.
 * @returns `"31st day of August, 2026"` (ordinal day, full month name, year),
 *   per the legal template's required wording.
 */
export function formatOrdinalDate(date: Date): string {
  const day   = date.getDate();
  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
  return `${day}${ordinalSuffix(day)} day of ${month}, ${date.getFullYear()}`;
}

/**
 * Formats a `Date` as `YYYY-MM-DD HH:MM:SS`, entirely in LOCAL time — the
 * wire format `affidavit_sign_datetime`/`affidavit_rejected_datetime` expect
 * (see `docs/openapispec.yml`'s example `"2023-07-26 17:00:00"`).
 *
 * @remarks
 * Fixes a 2026-06-11 defect where `AffidavitComponent` built this value by
 * concatenating `now.toISOString().split('T')[0]` (the **UTC** calendar
 * date) with `now.toTimeString().slice(0, 8)` (the **local** wall-clock
 * time) — two different reference frames glued into one string, which is
 * not a valid representation of any single instant/timezone and can read
 * up to a day off from the true local moment (e.g. a late-evening local
 * signing, after the UTC date has already rolled over). This function
 * builds every component from local `Date` getters, so the date and time
 * halves always describe the same real-world moment.
 *
 * @param date - The moment to format.
 * @returns e.g. `"2026-08-26 14:37:05"`.
 */
export function formatLocalDateTime(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
}
